import { ethers } from 'ethers';
import { supportInterfaceIds as supportInterfaceIds$1 } from '../../token-registry-v4/supportInterfaceIds';
import { supportInterfaceIds } from '../../token-registry-v5/supportInterfaceIds';
import { getEthersContractFromProvider } from '../../utils/ethers';
import { decrypt } from '../decrypt';
import { fetchEscrowTransfersV4, fetchEscrowTransfersV5 } from '../endorsement-chain/fetchEscrowTransfer';
import { fetchTokenTransfers } from '../endorsement-chain/fetchTokenTransfer';
import { mergeTransfersV4, mergeTransfersV5 } from '../endorsement-chain/helpers';
import { getEndorsementChain } from '../endorsement-chain/retrieveEndorsementChain';

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
const TitleEscrowInterface = {
  V4: supportInterfaceIds$1.TitleEscrow,
  V5: supportInterfaceIds.TitleEscrow
};
const getTitleEscrowFactoryAddress = /* @__PURE__ */ __name(async (tokenRegistryAddress, provider) => {
  const Contract = getEthersContractFromProvider(provider);
  const tokenRegistryAbi = ["function titleEscrowFactory() external view returns (address)"];
  const tokenRegistry = new Contract(tokenRegistryAddress, tokenRegistryAbi, provider);
  return await tokenRegistry.titleEscrowFactory();
}, "getTitleEscrowFactoryAddress");
const calldata = /* @__PURE__ */ __name(async (provider, functionSignature, contractAddress, functionTypes, params) => {
  const functionSelector = ethers.utils.id(functionSignature).slice(0, 10);
  const encodedParams = ethers.utils.defaultAbiCoder.encode(functionTypes, [...params]);
  const calldata2 = functionSelector + encodedParams.slice(2);
  const result = await provider.call({
    to: contractAddress,
    data: calldata2
  });
  return ethers.utils.getAddress(ethers.utils.hexDataSlice(result, 12));
}, "calldata");
const resolveTitleEscrowAddress = /* @__PURE__ */ __name(async (provider, titleEscrowFactoryAddress, tokenRegistryAddress, tokenId, options) => {
  try {
    if (options?.titleEscrowVersion === "v4") {
      return await calldata(
        provider,
        "getAddress(address,uint256)",
        titleEscrowFactoryAddress,
        ["address", "uint256"],
        [tokenRegistryAddress, tokenId]
      );
    }
    return await calldata(
      provider,
      "getEscrowAddress(address,uint256)",
      titleEscrowFactoryAddress,
      ["address", "uint256"],
      [tokenRegistryAddress, tokenId]
    );
  } catch {
    if (options?.titleEscrowVersion === "v4") {
      return await calldata(
        provider,
        "getEscrowAddress(address,uint256)",
        titleEscrowFactoryAddress,
        ["address", "uint256"],
        [tokenRegistryAddress, tokenId]
      );
    }
    return await calldata(
      provider,
      "getAddress(address,uint256)",
      titleEscrowFactoryAddress,
      ["address", "uint256"],
      [tokenRegistryAddress, tokenId]
    );
  }
}, "resolveTitleEscrowAddress");
const getTitleEscrowAddress = /* @__PURE__ */ __name(async (tokenRegistryAddress, tokenId, provider, options) => {
  const titleEscrowOwner = await getDocumentOwner(tokenRegistryAddress, tokenId, provider);
  const BURN_ADDRESS = "0x000000000000000000000000000000000000dEaD";
  const isInactiveEscrow = [BURN_ADDRESS, tokenRegistryAddress].map((address) => address.toLowerCase()).includes(titleEscrowOwner.toLowerCase());
  if (!isInactiveEscrow) return titleEscrowOwner;
  const titleEscrowFactoryAddress = await getTitleEscrowFactoryAddress(
    tokenRegistryAddress,
    provider
  );
  return resolveTitleEscrowAddress(
    provider,
    titleEscrowFactoryAddress,
    tokenRegistryAddress,
    tokenId,
    options
  );
}, "getTitleEscrowAddress");
const getDocumentOwner = /* @__PURE__ */ __name(async (tokenRegistryAddress, tokenId, provider) => {
  const Contract = getEthersContractFromProvider(provider);
  const tokenRegistryAbi = ["function ownerOf(uint256 tokenId) view returns (address)"];
  const tokenRegistry = new Contract(tokenRegistryAddress, tokenRegistryAbi, provider);
  return await tokenRegistry.ownerOf(tokenId);
}, "getDocumentOwner");
const checkSupportsInterface = /* @__PURE__ */ __name(async (contractAddress, interfaceId, provider) => {
  try {
    const Contract = getEthersContractFromProvider(provider);
    const abi = ["function supportsInterface(bytes4 interfaceId) external view returns (bool)"];
    const contract = new Contract(contractAddress, abi, provider);
    return await contract.supportsInterface(interfaceId);
  } catch {
    return false;
  }
}, "checkSupportsInterface");
const isTitleEscrowVersion = /* @__PURE__ */ __name(async ({
  tokenRegistryAddress,
  tokenId,
  titleEscrowAddress,
  versionInterface,
  provider
}) => {
  try {
    if (!titleEscrowAddress && (!tokenRegistryAddress || !tokenId)) {
      throw new Error("Missing required dependencies");
    } else if (!titleEscrowAddress) {
      titleEscrowAddress = await getTitleEscrowAddress(tokenRegistryAddress, tokenId, provider);
    }
    return await checkSupportsInterface(titleEscrowAddress, versionInterface, provider);
  } catch {
    return false;
  }
}, "isTitleEscrowVersion");
const fetchEndorsementChain = /* @__PURE__ */ __name(async (tokenRegistryAddress, tokenId, provider, keyId, titleEscrowAddress) => {
  if (!tokenRegistryAddress || !tokenId || !provider) {
    throw new Error("Missing required dependencies");
  }
  const resolvedTitleEscrowAddress = titleEscrowAddress ?? await getTitleEscrowAddress(tokenRegistryAddress, tokenId, provider);
  const [isV4, isV5] = await Promise.all([
    isTitleEscrowVersion({
      titleEscrowAddress: resolvedTitleEscrowAddress,
      versionInterface: TitleEscrowInterface.V4,
      provider
    }),
    isTitleEscrowVersion({
      titleEscrowAddress: resolvedTitleEscrowAddress,
      versionInterface: TitleEscrowInterface.V5,
      provider
    })
  ]);
  if (!isV4 && !isV5) {
    throw new Error("Only Token Registry V4/V5 is supported");
  }
  let transferEvents = [];
  if (isV4) {
    const [tokenLogs, titleEscrowLogs] = await Promise.all([
      fetchTokenTransfers(provider, tokenRegistryAddress, tokenId),
      fetchEscrowTransfersV4(provider, resolvedTitleEscrowAddress)
    ]);
    transferEvents = mergeTransfersV4([...titleEscrowLogs, ...tokenLogs]);
  } else if (isV5) {
    const titleEscrowLogs = await fetchEscrowTransfersV5(
      provider,
      resolvedTitleEscrowAddress,
      tokenRegistryAddress
    );
    transferEvents = mergeTransfersV5(titleEscrowLogs);
  }
  const endorsementChain = await getEndorsementChain(provider, transferEvents);
  return isV4 ? endorsementChain : endorsementChain.map((event) => ({
    ...event,
    remark: event?.remark?.slice(2) ? decrypt(event.remark.slice(2), keyId ?? "") : ""
  }));
}, "fetchEndorsementChain");

export { TitleEscrowInterface, checkSupportsInterface, fetchEndorsementChain, getDocumentOwner, getTitleEscrowAddress, isTitleEscrowVersion };
